<template>
  <div class="mail-template-databank">
    <nav class="vp-crumbs" :aria-label="$t('databank.mailTemplate.title')">
      <router-link to="/databank">{{ $t('databank.title') }}</router-link>
      <span class="vp-crumbs-sep">/</span>
      <span>{{ $t('databank.mailTemplate.title') }}</span>
    </nav>

    <div class="vp-page-head">
      <div class="vp-min-w-0">
        <h1 class="vp-page-title">{{ $t('databank.mailTemplate.title') }}</h1>
        <div class="vp-page-sub">
          <i class="ph-duotone ph-envelope-simple"></i>
          <span>{{ $t('databank.mailTemplate.description') }}</span>
        </div>
      </div>
      <div class="vp-page-actions">
        <button type="button" class="vp-btn vp-btn-primary" :disabled="isLoadingData" @click="loadMailTemplates()">
          <i class="ph-duotone ph-arrow-clockwise" :class="{ 'vp-spin': isLoadingData }"></i>
          {{ $t('common.refresh') }}
        </button>
      </div>
    </div>

    <div v-if="error" class="vp-note vp-note--danger vp-stack-bottom">
      <i class="ph-duotone ph-warning-circle"></i>
      <div class="vp-min-w-0">{{ error }}</div>
      <button type="button" class="vp-note-dismiss" :aria-label="$t('common.close')" @click="error = null">
        <i class="ph-duotone ph-x"></i>
      </button>
    </div>

    <div class="vp-filterbar">
      <div class="vp-field vp-field--grow">
        <i class="ph-duotone ph-magnifying-glass vp-field-icon"></i>
        <input
          v-model="searchQuery"
          type="search"
          class="vp-input"
          :placeholder="$t('common.actions.search')"
          :aria-label="$t('common.actions.search')"
        />
      </div>
      <div class="vp-filterbar-end">
        <button
          v-if="searchQuery"
          type="button"
          class="vp-btn vp-btn-ghost vp-btn-sm"
          :title="$t('common.clear')"
          @click="clearSearch"
        >
          <i class="ph-duotone ph-x"></i>{{ $t('common.clear') }}
        </button>
      </div>
    </div>

    <section class="vp-card">
      <div class="vp-card-head">
        <h6 class="vp-card-title">
          {{ $t('databank.mailTemplate.existingRecords') }}
          <span class="vp-chip">{{ $formatInteger(filteredGroups.length) }}</span>
        </h6>
      </div>

      <div v-if="isLoadingData" class="vp-loading"><span class="spinner-border spinner-border-sm"></span></div>

      <div v-else-if="filteredGroups.length" class="vp-table-wrap">
        <table class="vp-table" :aria-label="$t('databank.mailTemplate.title')">
          <thead>
            <tr>
              <th>{{ $t('databank.mailTemplate.key') }}</th>
              <th>{{ $t('databank.mailTemplate.subjectEnglish') }}</th>
              <th>{{ $t('databank.mailTemplate.languages') }}</th>
              <th>{{ $t('databank.mailTemplate.lastModified') }}</th>
              <th class="text-end">{{ $t('common.actionsLabel') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="group in filteredGroups" :key="group.templateKey">
              <td>
                <span v-if="group.templateKey" class="vp-mono">{{ group.templateKey }}</span>
                <span v-else class="vp-text-muted">—</span>
              </td>
              <td class="vp-cell-strong">{{ group.enSubject || '—' }}</td>
              <td>
                <div class="culture-badges">
                  <span
                    v-for="code in group.cultureCodes"
                    :key="code"
                    class="vp-chip vp-mono"
                    :class="isEnglish(code) ? 'vp-chip--primary' : 'vp-chip--muted'"
                    :title="cultureDisplayName(code)"
                  >
                    {{ code.toUpperCase() }}
                  </span>
                </div>
              </td>
              <td class="vp-num text-nowrap">{{ group.lastModified ? formatDateTime(group.lastModified) : '—' }}</td>
              <td class="text-end">
                <div class="vp-row-actions">
                  <button
                    type="button"
                    class="vp-icon-btn"
                    :title="$t('common.edit')"
                    :disabled="isLoading"
                    @click="openTemplate(group)"
                  >
                    <i class="ph-duotone ph-pencil-simple"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="vp-empty">
        <i class="ph-duotone ph-envelope-simple vp-empty-icon"></i>
        <div class="vp-empty-title">{{ $t('databank.mailTemplate.title') }}</div>
        <div class="vp-empty-text">{{ $t('databank.mailTemplate.noRecords') }}</div>
      </div>
    </section>

    <!-- Edit one template key, one language at a time -->
    <div class="modal fade" id="mailTemplateModal" tabindex="-1" aria-labelledby="mailTemplateModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content vp-modal">
          <div class="vp-modal-head">
            <h5 class="vp-modal-title" id="mailTemplateModalLabel">
              <i class="ph-duotone ph-envelope-simple"></i>
              {{ $t('databank.mailTemplate.editTitle') }}
              <small class="vp-mono">{{ activeKey }}</small>
            </h5>
            <button type="button" class="vp-modal-close" data-bs-dismiss="modal" :aria-label="$t('common.close')">
              <i class="ph-duotone ph-x"></i>
            </button>
          </div>

          <div class="vp-modal-body vp-modal-body--full">
            <div class="lang-bar vp-stack-bottom">
              <div class="vp-form-field lang-field">
                <label class="vp-label" for="mailTemplateCulture">{{ $t('databank.mailTemplate.language') }}</label>
                <select
                  id="mailTemplateCulture"
                  class="vp-select"
                  :value="selectedCulture"
                  :disabled="isLoading"
                  @change="onCultureChange"
                >
                  <option v-for="option in cultureOptions" :key="option.cultureName" :value="option.cultureName">
                    {{ cultureOptionLabel(option) }}
                  </option>
                </select>
              </div>
              <div class="lang-bar-actions">
                <button
                  type="button"
                  class="vp-btn vp-btn-ghost vp-btn-sm"
                  :disabled="isLoading"
                  :title="$t('databank.mailTemplate.restore')"
                  @click="restoreSelectedCulture"
                >
                  <span v-if="busyAction === 'restore'" class="spinner-border spinner-border-sm"></span>
                  <i v-else class="ph-duotone ph-arrow-counter-clockwise"></i>
                  {{ $t('databank.mailTemplate.restore') }}
                </button>
                <button
                  v-if="currentRow && !isEnglishSelected"
                  type="button"
                  class="vp-btn vp-btn-ghost vp-btn-sm"
                  :disabled="isLoading"
                  :title="$t('databank.mailTemplate.deleteTranslation')"
                  @click="deleteSelectedTranslation"
                >
                  <span v-if="busyAction === 'delete'" class="spinner-border spinner-border-sm"></span>
                  <i v-else class="ph-duotone ph-trash"></i>
                  {{ $t('databank.mailTemplate.deleteTranslation') }}
                </button>
              </div>
            </div>

            <!-- The selected language has no row of its own: mails fall back to English. -->
            <div v-if="!currentRow" class="vp-note vp-note--info vp-stack-bottom">
              <i class="ph-duotone ph-translate"></i>
              <div class="vp-min-w-0">
                <template v-if="isEnglishSelected">
                  <div class="vp-cell-strong">{{ $t('databank.mailTemplate.englishMissing') }}</div>
                </template>
                <template v-else>
                  <div class="vp-cell-strong">{{ $t('databank.mailTemplate.notTranslatedTitle') }}</div>
                  <div>{{ $t('databank.mailTemplate.notTranslatedText', { language: selectedCultureDisplayName }) }}</div>
                  <div v-if="!englishRow" class="vp-text-muted">{{ $t('databank.mailTemplate.englishMissing') }}</div>
                </template>
              </div>
              <button
                v-if="!isEnglishSelected"
                type="button"
                class="vp-btn vp-btn-primary vp-btn-sm"
                :disabled="isLoading || !englishRow"
                @click="createFromEnglish"
              >
                <span v-if="busyAction === 'create'" class="spinner-border spinner-border-sm"></span>
                <i v-else class="ph-duotone ph-copy"></i>
                {{ $t('databank.mailTemplate.createFromEnglish') }}
              </button>
            </div>

            <form v-show="currentRow" @submit.prevent="handleSave(false)">
              <div class="vp-form-grid vp-stack-bottom">
                <div class="vp-form-field">
                  <label class="vp-label" for="mailTemplateKey">{{ $t('databank.mailTemplate.key') }}</label>
                  <input id="mailTemplateKey" :value="activeKey" type="text" class="vp-control vp-mono" disabled />
                </div>

                <div class="vp-form-field">
                  <label class="vp-label" for="mailTemplateSubject">
                    {{ $t('databank.mailTemplate.subject') }} <span class="vp-label-req">*</span>
                  </label>
                  <input
                    id="mailTemplateSubject"
                    v-model="form.subject"
                    type="text"
                    class="vp-control"
                    :class="{ 'is-invalid': v$.subject.$error }"
                    :placeholder="$t('databank.mailTemplate.subjectPlaceholder')"
                    required
                  />
                  <div v-if="v$.subject.$error" class="vp-help vp-help--error">
                    {{ v$.subject.$errors[0].$message }}
                  </div>
                </div>
              </div>

              <div class="vp-panel">
                <button
                  type="button"
                  class="vp-panel-head"
                  data-bs-toggle="collapse"
                  data-bs-target="#htmlBodyCollapse"
                  :aria-expanded="isHtmlBodyExpanded"
                  aria-controls="htmlBodyCollapse"
                >
                  <i class="ph-duotone ph-code"></i>
                  {{ $t('databank.mailTemplate.htmlBody') }} <span class="vp-label-req">*</span>
                  <i class="ph-duotone ph-caret-right vp-panel-caret"></i>
                </button>

                <div id="htmlBodyCollapse" class="collapse show" :class="{ show: isHtmlBodyExpanded }">
                  <!-- A template that has lost one of these will not render on send, so the
                       missing ones are called out before saving is allowed. They are taken from
                       the English body so a translation cannot silently drop one. -->
                  <div v-if="requiredPlaceholders.length > 0" class="placeholder-bar">
                    <div class="vp-kv-label">
                      {{ $t('databank.mailTemplate.requiredPlaceholders') }}
                      <span v-if="!isEnglishSelected && englishRow" class="vp-text-muted placeholder-source">
                        {{ $t('databank.mailTemplate.placeholdersFromEnglish') }}
                      </span>
                    </div>
                    <div class="placeholder-list">
                      <span
                        v-for="placeholder in requiredPlaceholders"
                        :key="placeholder"
                        class="vp-chip vp-mono"
                        :class="isPlaceholderMissing(placeholder) ? 'vp-chip--danger' : 'vp-chip--success'"
                      >
                        {{ placeholder }}
                      </span>
                    </div>
                  </div>

                  <div class="editor-split">
                    <div class="editor-pane">
                      <div class="editor-pane-head">
                        <span>{{ $t('databank.mailTemplate.codeEditor') }}</span>
                      </div>
                      <div class="editor-pane-body">
                        <div
                          ref="codeEditorContainer"
                          class="code-editor-wrapper"
                          :class="{ 'is-invalid': v$.htmlBody.$error }"
                        ></div>
                        <div v-if="v$.htmlBody.$error" class="vp-help vp-help--error editor-error">
                          {{ v$.htmlBody.$errors[0].$message }}
                        </div>
                      </div>
                    </div>

                    <div class="editor-pane">
                      <div class="editor-pane-head">
                        <span>{{ $t('databank.mailTemplate.preview') }}</span>
                        <button
                          type="button"
                          class="vp-btn vp-btn-ghost vp-btn-sm"
                          :disabled="!form.htmlBody"
                          :title="$t('databank.mailTemplate.fullscreenHint')"
                          @click="showPreviewFromForm"
                        >
                          <i class="ph-duotone ph-arrows-out"></i>{{ $t('databank.mailTemplate.fullscreen') }}
                        </button>
                      </div>
                      <div class="editor-pane-body editor-pane-body--preview">
                        <div v-if="!form.htmlBody" class="vp-empty">
                          <i class="ph-duotone ph-eye-slash vp-empty-icon"></i>
                          <div class="vp-empty-text">{{ $t('databank.mailTemplate.nothingToPreview') }}</div>
                        </div>
                        <iframe
                          v-else
                          ref="previewIframe"
                          :srcdoc="form.htmlBody"
                          class="preview-frame"
                          :title="$t('databank.mailTemplate.previewTitle')"
                          sandbox="allow-same-origin"
                        ></iframe>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div class="vp-modal-foot">
            <template v-if="currentRow">
              <button
                type="button"
                class="vp-btn vp-btn-ghost"
                :disabled="isLoading || !allPlaceholdersPresent"
                @click="handleSave(false)"
              >
                <span v-if="busyAction === 'save'" class="spinner-border spinner-border-sm"></span>
                <i v-else class="ph-duotone ph-floppy-disk"></i>
                {{ busyAction === 'save' ? $t('common.saving') : $t('common.save') }}
              </button>
              <button
                type="button"
                class="vp-btn vp-btn-primary"
                :disabled="isLoading || !allPlaceholdersPresent"
                @click="handleSave(true)"
              >
                <span v-if="busyAction === 'saveAndClose'" class="spinner-border spinner-border-sm"></span>
                <i v-else class="ph-duotone ph-check"></i>
                {{ busyAction === 'saveAndClose' ? $t('common.savingAndClosing') : $t('common.saveAndClose') }}
              </button>
            </template>
            <button type="button" class="vp-btn vp-btn-ghost" data-bs-dismiss="modal">
              {{ $t('common.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Fullscreen HTML preview -->
    <div class="modal fade" id="htmlPreviewModal" tabindex="-1" aria-labelledby="htmlPreviewModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content vp-modal">
          <div class="vp-modal-head">
            <h5 class="vp-modal-title" id="htmlPreviewModalLabel">
              <i class="ph-duotone ph-eye"></i>
              {{ $t('databank.mailTemplate.previewTitle') }}
              <small class="vp-mono">{{ previewTemplateKey }}</small>
            </h5>
            <button type="button" class="vp-modal-close" data-bs-dismiss="modal" :aria-label="$t('common.close')">
              <i class="ph-duotone ph-x"></i>
            </button>
          </div>

          <div class="vp-modal-body vp-modal-body--full preview-body">
            <iframe
              :srcdoc="previewHtmlContent"
              class="preview-frame preview-frame--full"
              :title="$t('databank.mailTemplate.previewTitle')"
              sandbox="allow-same-origin"
            ></iframe>
          </div>

          <div class="vp-modal-foot">
            <button type="button" class="vp-btn vp-btn-primary" data-bs-dismiss="modal">
              {{ $t('common.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { Modal } from 'bootstrap';
import Swal from 'sweetalert2';
import { EditorView, keymap } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { basicSetup } from 'codemirror';
import { html } from '@codemirror/lang-html';
import { defaultKeymap } from '@codemirror/commands';
import mailTemplateService from '@/services/mailTemplateService';
import { useDateFormatter } from '@/composables/useDateFormatter';

// English is the fallback language: it always exists and cannot be deleted.
const DEFAULT_CULTURE = 'en';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { formatDateTime } = useDateFormatter();

const error = ref(null);
const isLoadingData = ref(false);
const isLoading = ref(false);
const busyAction = ref(null);
const searchQuery = ref('');
const previewHtmlContent = ref('');
const previewTemplateKey = ref('');
const codeEditorContainer = ref(null);
const previewIframe = ref(null);
const isHtmlBodyExpanded = ref(true);

const rows = ref([]);
const cultures = ref([]);
const activeKey = ref('');
const selectedCulture = ref(DEFAULT_CULTURE);
const snapshot = ref({ subject: '', htmlBody: '' });

let codeEditor = null;
let mailTemplateModal = null;
let htmlPreviewModal = null;
let accordionShowHandler = null;
let accordionHideHandler = null;
let previewUpdateTimer = null;

const form = ref({
  subject: '',
  htmlBody: '',
  plainTextBody: ''
});

const currentUser = computed(() => store.getters['auth/currentUser']);

const rules = computed(() => ({
  subject: { required },
  htmlBody: { required }
}));

const v$ = useVuelidate(rules, form);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const normalizeCulture = (code) => (code || '').toLowerCase();
const isEnglish = (code) => normalizeCulture(code) === DEFAULT_CULTURE;

const compareCultures = (a, b) => {
  if (isEnglish(a) !== isEnglish(b)) return isEnglish(a) ? -1 : 1;
  return a.localeCompare(b);
};

const rowTimestamp = (row) => {
  const value = row.lastModificationTime || row.creationTime;
  return value ? new Date(value).getTime() : 0;
};

const normalizeRow = (row) => ({
  id: row.id,
  tenantId: row.tenantId ?? null,
  templateKey: row.templateKey || '',
  cultureName: row.cultureName || DEFAULT_CULTURE,
  subject: row.subject || '',
  htmlBody: row.htmlBody || '',
  plainTextBody: row.plainTextBody || '',
  creationTime: row.creationTime || null,
  lastModificationTime: row.lastModificationTime || null
});

const apiErrorMessage = (err, fallbackKey) => err?.response?.data?.error?.message || t(fallbackKey);

const notify = (message, type) => store.dispatch('notify', { message, type });

// Placeholders like {verificationUrl}; CSS blocks are stripped first so `{ color: red; }` is not matched.
const extractPlaceholders = (htmlContent) => {
  if (!htmlContent) return [];

  const content = htmlContent
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/style\s*=\s*["'][^"']*["']/gi, '')
    .replace(/style\s*=\s*\{[^}]*\}/gi, '')
    .replace(/\{[^}]*[:;][^}]*\}/g, '');

  const found = new Set();
  const placeholderPattern = /\{([a-zA-Z]\w*)\}/g;
  let match;
  while ((match = placeholderPattern.exec(content)) !== null) {
    found.add(`{${match[1]}}`);
  }
  return [...found].sort((a, b) => a.localeCompare(b));
};

// ---------------------------------------------------------------------------
// List (one row per template key)
// ---------------------------------------------------------------------------

const cultureDisplayName = (code) => {
  const culture = cultures.value.find(c => normalizeCulture(c.cultureName) === normalizeCulture(code));
  return culture?.displayName || code;
};

const buildGroup = (templateKey, groupRows) => {
  const enRow = groupRows.find(r => isEnglish(r.cultureName));
  const timestamps = groupRows.map(rowTimestamp).filter(Boolean);
  return {
    templateKey,
    enSubject: enRow?.subject || '',
    cultureCodes: groupRows.map(r => r.cultureName).sort(compareCultures),
    subjects: groupRows.map(r => r.subject.toLowerCase()),
    lastModified: timestamps.length ? new Date(Math.max(...timestamps)) : null
  };
};

const groups = computed(() => {
  const byKey = new Map();
  for (const row of rows.value) {
    if (!byKey.has(row.templateKey)) byKey.set(row.templateKey, []);
    byKey.get(row.templateKey).push(row);
  }
  return [...byKey.entries()]
    .map(([key, groupRows]) => buildGroup(key, groupRows))
    .sort((a, b) => a.templateKey.localeCompare(b.templateKey));
});

// Every row is loaded once, so search filters locally and keeps each key's language set complete.
const filteredGroups = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return groups.value;
  return groups.value.filter(group =>
    group.templateKey.toLowerCase().includes(query) || group.subjects.some(s => s.includes(query))
  );
});

const loadMailTemplates = async () => {
  isLoadingData.value = true;
  error.value = null;
  try {
    const response = await mailTemplateService.searchMailTemplates({ skipCount: 0, maxResultCount: 1000 });
    const items = Array.isArray(response) ? response : response?.items;
    rows.value = (items || []).map(normalizeRow);
  } catch (err) {
    console.error('Error loading mail templates:', err);
    error.value = apiErrorMessage(err, 'databank.mailTemplate.errors.loadError');
    notify(error.value, 'error');
  } finally {
    isLoadingData.value = false;
  }
};

const loadCultures = async () => {
  try {
    const response = await mailTemplateService.getCultures();
    cultures.value = Array.isArray(response) ? response : [];
  } catch (err) {
    console.error('Error loading cultures:', err);
    cultures.value = [];
    notify(apiErrorMessage(err, 'databank.mailTemplate.errors.loadCulturesError'), 'error');
  }
};

const clearSearch = () => {
  searchQuery.value = '';
};

// ---------------------------------------------------------------------------
// Editor state (one key, one language at a time)
// ---------------------------------------------------------------------------

const rowsForActiveKey = computed(() => rows.value.filter(r => r.templateKey === activeKey.value));

const findRow = (culture) =>
  rowsForActiveKey.value.find(r => normalizeCulture(r.cultureName) === normalizeCulture(culture)) || null;

const currentRow = computed(() => findRow(selectedCulture.value));
const englishRow = computed(() => findRow(DEFAULT_CULTURE));
const isEnglishSelected = computed(() => isEnglish(selectedCulture.value));

// Languages from /cultures, plus any language a row exists for that the list does not know.
const cultureOptions = computed(() => {
  const existing = new Set(rowsForActiveKey.value.map(r => normalizeCulture(r.cultureName)));
  const options = cultures.value.map(c => ({
    cultureName: c.cultureName,
    displayName: c.displayName || c.cultureName,
    exists: existing.has(normalizeCulture(c.cultureName))
  }));
  const known = new Set(options.map(o => normalizeCulture(o.cultureName)));
  for (const row of rowsForActiveKey.value) {
    if (!known.has(normalizeCulture(row.cultureName))) {
      options.push({ cultureName: row.cultureName, displayName: row.cultureName, exists: true });
      known.add(normalizeCulture(row.cultureName));
    }
  }
  return options.sort((a, b) => compareCultures(a.cultureName, b.cultureName));
});

const cultureOptionLabel = (option) =>
  option.exists
    ? t('databank.mailTemplate.languageOptionTranslated', { language: option.displayName })
    : option.displayName;

const selectedCultureDisplayName = computed(() => cultureDisplayName(selectedCulture.value));

// A translation must keep every placeholder the English body uses.
const requiredPlaceholders = computed(() =>
  extractPlaceholders(englishRow.value?.htmlBody || currentRow.value?.htmlBody || '')
);

const isPlaceholderMissing = (placeholder) => !form.value.htmlBody?.includes(placeholder);

const allPlaceholdersPresent = computed(() =>
  requiredPlaceholders.value.every(placeholder => !isPlaceholderMissing(placeholder))
);

const isDirty = computed(() =>
  Boolean(currentRow.value) &&
  (form.value.subject !== snapshot.value.subject || form.value.htmlBody !== snapshot.value.htmlBody)
);

const loadCultureIntoForm = (culture) => {
  selectedCulture.value = culture;
  const row = findRow(culture);
  form.value = {
    subject: row?.subject || '',
    htmlBody: row?.htmlBody || '',
    plainTextBody: row?.plainTextBody || ''
  };
  snapshot.value = { subject: form.value.subject, htmlBody: form.value.htmlBody };
  v$.value.$reset();
};

const upsertRow = (row) => {
  const index = rows.value.findIndex(r => r.id === row.id);
  if (index === -1) {
    rows.value.push(row);
  } else {
    rows.value[index] = row;
  }
};

const openTemplate = async (group) => {
  activeKey.value = group.templateKey;
  const hasEnglish = group.cultureCodes.some(isEnglish);
  loadCultureIntoForm(hasEnglish ? DEFAULT_CULTURE : group.cultureCodes[0] || DEFAULT_CULTURE);
  isHtmlBodyExpanded.value = true;
  if (mailTemplateModal) {
    mailTemplateModal.show();
    await nextTick();
    initCodeEditor();
  }
};

const confirmDiscardChanges = async () => {
  const result = await Swal.fire({
    title: t('databank.mailTemplate.unsavedChangesTitle'),
    text: t('databank.mailTemplate.unsavedChangesMessage'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('databank.mailTemplate.discardChanges'),
    cancelButtonText: t('common.cancel'),
    customClass: {
      confirmButton: 'btn btn-danger',
      cancelButton: 'btn btn-outline-secondary'
    },
    buttonsStyling: false
  });
  return result.isConfirmed;
};

const onCultureChange = async (event) => {
  const target = event.target;
  const nextCulture = target.value;
  if (isDirty.value && !(await confirmDiscardChanges())) {
    // Keep the select on the language whose edits are still in the form.
    target.value = selectedCulture.value;
    return;
  }
  loadCultureIntoForm(nextCulture);
};

const runBusy = async (action, work) => {
  isLoading.value = true;
  busyAction.value = action;
  try {
    await work();
  } finally {
    isLoading.value = false;
    busyAction.value = null;
  }
};

const handleSave = async (shouldClose = false) => {
  const row = currentRow.value;
  if (!row || !allPlaceholdersPresent.value) return;
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  error.value = null;
  await runBusy(shouldClose ? 'saveAndClose' : 'save', async () => {
    try {
      const payload = {
        templateKey: row.templateKey,
        cultureName: row.cultureName,
        subject: form.value.subject,
        htmlBody: form.value.htmlBody,
        plainTextBody: form.value.plainTextBody
      };
      const response = await mailTemplateService.updateMailTemplate(row.id, payload);
      upsertRow(normalizeRow({ ...row, ...payload, ...response, id: row.id }));
      snapshot.value = { subject: form.value.subject, htmlBody: form.value.htmlBody };
      notify(t('databank.mailTemplate.updateSuccess'), 'success');
      if (shouldClose && mailTemplateModal) mailTemplateModal.hide();
    } catch (err) {
      console.error('Save error:', err);
      notify(apiErrorMessage(err, 'databank.mailTemplate.errors.saveError'), 'error');
    }
  });
};

const createFromEnglish = async () => {
  const source = englishRow.value;
  if (!source || currentRow.value) return;
  const culture = selectedCulture.value;

  await runBusy('create', async () => {
    try {
      const payload = {
        templateKey: source.templateKey,
        cultureName: culture,
        subject: source.subject,
        htmlBody: source.htmlBody,
        plainTextBody: source.plainTextBody
      };
      const response = await mailTemplateService.createMailTemplate(payload);
      upsertRow(normalizeRow({ ...payload, ...response }));
      loadCultureIntoForm(culture);
      notify(t('databank.mailTemplate.createTranslationSuccess'), 'success');
    } catch (err) {
      console.error('Create translation error:', err);
      notify(apiErrorMessage(err, 'databank.mailTemplate.errors.saveError'), 'error');
    }
  });
};

const deleteSelectedTranslation = async () => {
  const row = currentRow.value;
  if (!row || isEnglish(row.cultureName)) return;

  const result = await Swal.fire({
    title: t('databank.mailTemplate.deleteTranslationConfirmTitle'),
    text: t('databank.mailTemplate.deleteTranslationConfirmMessage', { language: selectedCultureDisplayName.value }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('common.delete'),
    cancelButtonText: t('common.cancel'),
    customClass: {
      confirmButton: 'btn btn-danger',
      cancelButton: 'btn btn-outline-secondary'
    },
    buttonsStyling: false
  });
  if (!result.isConfirmed) return;

  await runBusy('delete', async () => {
    try {
      await mailTemplateService.deleteMailTemplate(row.id);
      rows.value = rows.value.filter(r => r.id !== row.id);
      loadCultureIntoForm(selectedCulture.value);
      notify(t('databank.mailTemplate.deleteTranslationSuccess'), 'success');
    } catch (err) {
      console.error('Delete translation error:', err);
      notify(apiErrorMessage(err, 'databank.mailTemplate.errors.deleteError'), 'error');
    }
  });
};

const confirmRestore = async () => {
  const result = await Swal.fire({
    title: t('databank.mailTemplate.restoreConfirmTitle'),
    html: `
      <div class="text-start">
        <p>${t('databank.mailTemplate.restoreConfirmMessage')}</p>
        <p><strong>${selectedCultureDisplayName.value}</strong></p>
        <p class="text-danger mt-3"><strong>${t('databank.mailTemplate.restoreWarning')}</strong></p>
        <p class="text-muted mt-2">${t('databank.mailTemplate.restoreWarningDetails')}</p>
      </div>
    `,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('databank.mailTemplate.restoreConfirmButton'),
    cancelButtonText: t('common.cancel'),
    customClass: {
      confirmButton: 'btn btn-danger',
      cancelButton: 'btn btn-outline-secondary'
    },
    buttonsStyling: false
  });
  return result.isConfirmed;
};

const restoreSelectedCulture = async () => {
  if (!activeKey.value) {
    notify(t('databank.mailTemplate.errors.invalidTemplateKey'), 'error');
    return;
  }
  if (!(await confirmRestore())) return;
  const culture = selectedCulture.value;

  await runBusy('restore', async () => {
    try {
      await mailTemplateService.restoreHostTemplateByKey(activeKey.value, culture);
      await loadMailTemplates();
      loadCultureIntoForm(culture);
      notify(t('databank.mailTemplate.restoreSuccess'), 'success');
    } catch (err) {
      console.error('Restore error:', err);
      notify(apiErrorMessage(err, 'databank.mailTemplate.errors.restoreError'), 'error');
    }
  });
};

const resetEditor = () => {
  activeKey.value = '';
  selectedCulture.value = DEFAULT_CULTURE;
  form.value = { subject: '', htmlBody: '', plainTextBody: '' };
  snapshot.value = { subject: '', htmlBody: '' };
  isHtmlBodyExpanded.value = true;
  v$.value.$reset();
};

const showPreviewFromForm = () => {
  if (!form.value.htmlBody) return;
  previewTemplateKey.value = `${activeKey.value} · ${selectedCulture.value.toUpperCase()}`;
  previewHtmlContent.value = form.value.htmlBody;
  htmlPreviewModal?.show();
};

// ---------------------------------------------------------------------------
// CodeMirror
// ---------------------------------------------------------------------------

const initCodeEditor = () => {
  if (!codeEditorContainer.value || codeEditor) return;

  try {
    const state = EditorState.create({
      doc: form.value.htmlBody || '',
      extensions: [
        basicSetup,
        html(),
        keymap.of(defaultKeymap),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            form.value.htmlBody = update.state.doc.toString();
          }
        }),
        EditorView.theme({
          '&': { fontSize: '14px', border: 'none', borderRadius: '0' },
          '.cm-content': { padding: '12px' },
          '.cm-focused': { outline: 'none' },
          '.cm-scroller': { fontFamily: 'monospace' }
        })
      ]
    });

    codeEditor = new EditorView({ state, parent: codeEditorContainer.value });
  } catch (err) {
    console.error('Error initializing code editor:', err);
  }
};

const destroyCodeEditor = () => {
  if (!codeEditor) return;
  try {
    codeEditor.destroy();
  } catch (err) {
    console.error('Error destroying code editor:', err);
  } finally {
    codeEditor = null;
  }
};

const writePreview = (content) => {
  if (!previewIframe.value || !content) return;
  try {
    const iframeDoc = previewIframe.value.contentDocument || previewIframe.value.contentWindow.document;
    if (iframeDoc) {
      iframeDoc.open();
      iframeDoc.write(content);
      iframeDoc.close();
    }
  } catch (err) {
    console.error('Error writing preview:', err);
    previewIframe.value.srcdoc = content;
  }
};

// Keep CodeMirror in sync when the form is loaded programmatically (language switch, restore).
watch(() => form.value.htmlBody, (newValue) => {
  if (codeEditor && codeEditor.state.doc.toString() !== newValue) {
    codeEditor.dispatch(codeEditor.state.update({
      changes: { from: 0, to: codeEditor.state.doc.length, insert: newValue || '' }
    }));
  }

  clearTimeout(previewUpdateTimer);
  previewUpdateTimer = setTimeout(() => {
    nextTick(() => writePreview(newValue));
  }, 300);
});

// The editor lives in a v-show form; re-measure once it becomes visible again.
watch(currentRow, (row) => {
  if (row && codeEditor) nextTick(() => codeEditor?.requestMeasure());
});

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

const unbindAccordion = () => {
  const htmlBodyCollapse = document.getElementById('htmlBodyCollapse');
  if (!htmlBodyCollapse) return;
  if (accordionShowHandler) htmlBodyCollapse.removeEventListener('show.bs.collapse', accordionShowHandler);
  if (accordionHideHandler) htmlBodyCollapse.removeEventListener('hide.bs.collapse', accordionHideHandler);
};

const bindAccordion = () => {
  const htmlBodyCollapse = document.getElementById('htmlBodyCollapse');
  if (!htmlBodyCollapse) return;
  unbindAccordion();
  accordionShowHandler = () => { isHtmlBodyExpanded.value = true; };
  accordionHideHandler = () => { isHtmlBodyExpanded.value = false; };
  htmlBodyCollapse.addEventListener('show.bs.collapse', accordionShowHandler);
  htmlBodyCollapse.addEventListener('hide.bs.collapse', accordionHideHandler);
  isHtmlBodyExpanded.value = htmlBodyCollapse.classList.contains('show');
};

onMounted(() => {
  if (!currentUser.value?.isSuperAdmin) {
    notify(t('common.notification.unauthorized'), 'error');
    router.push('/databank');
    return;
  }

  const modalElement = document.getElementById('mailTemplateModal');
  if (modalElement) {
    mailTemplateModal = new Modal(modalElement, { focus: false });
    modalElement.addEventListener('shown.bs.modal', () => {
      nextTick(() => {
        initCodeEditor();
        bindAccordion();
      });
    });
    modalElement.addEventListener('hidden.bs.modal', () => {
      destroyCodeEditor();
      resetEditor();
      unbindAccordion();
    });
  }

  const previewModalElement = document.getElementById('htmlPreviewModal');
  if (previewModalElement) {
    htmlPreviewModal = new Modal(previewModalElement, { focus: false });
  }

  loadCultures();
  loadMailTemplates();
});

onBeforeUnmount(() => {
  destroyCodeEditor();
  clearTimeout(previewUpdateTimer);
});
</script>

<style scoped>
/* The editor modal is full-screen: its body takes the height left between header and
   footer and scrolls itself. Without flex: 1 it grew past the screen and, with the
   dialog clipping it, the lower part (editor, preview) could not be reached. */
.vp-modal-body--full {
  max-height: none;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.lang-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.lang-field {
  min-width: 260px;
}

.lang-bar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.culture-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.placeholder-source {
  margin-left: 6px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}

.placeholder-bar {
  padding: 10px 14px;
  border-bottom: 1px solid var(--vp-gray-200);
  background: var(--vp-gray-100);
}

.placeholder-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.editor-split {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  min-height: 500px;
  height: calc(100vh - 420px);
}

.editor-pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-right: 1px solid var(--vp-gray-200);
}

.editor-pane:last-child {
  border-right: 0;
}

.editor-pane-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--vp-gray-200);
  background: var(--vp-gray-100);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-gray-600);
}

.editor-pane-body {
  position: relative;
  flex: 1;
  min-height: 0;
}

.editor-pane-body--preview {
  background: var(--vp-gray-100);
}

.editor-error {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 6px 10px;
}

.preview-frame {
  width: 100%;
  height: 100%;
  border: 0;
  background: var(--vp-surface);
}

.preview-body {
  padding: 0;
  flex: 1;
  min-height: 0;
}

.preview-frame--full {
  min-height: calc(100vh - 140px);
}

/* Pinned to the pane rather than height: 100%: the pane is a flex item, so a percentage
   height did not resolve and the editor grew with its content instead of scrolling. */
.code-editor-wrapper {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.code-editor-wrapper.is-invalid {
  box-shadow: inset 0 0 0 1px var(--vp-danger);
}

.code-editor-wrapper :deep(.cm-editor) {
  height: 100%;
}

.code-editor-wrapper :deep(.cm-scroller) {
  overflow: auto;
  height: 100%;
}
</style>
